import $ from "jquery";
import { useState } from "react";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import useRDService from "../../../CustomHooks/useRDService";
import DeviceStatus from "./DeviceStatus";
import SubhearnBank5AepsForm from "./SubhearnBank5AepsForm";

const generateRef = () => `REF${Date.now()}${Math.floor(Math.random() * 1000)}`;
const generateTs  = () => `${Date.now()}`;

const SubhearnBank5AepsIntegration = () => {
  const { device, captureFingerprint, loading } = useRDService();

  const merchantdetails = useSelector((s) => s.auth?.employeeDetails);
  const { lat, lng }    = useSelector((s) => s.location);

  const [processing,       setProcessing]       = useState(false);
  const [previousData,     setPreviousData]     = useState(null);
  const [clearFormTrigger, setClearFormTrigger] = useState(0);

  const userId    = merchantdetails?.username;
  const latitude  = lat?.toString() || "";
  const longitude = lng?.toString() || "";

  const { callApi: balanceEnquiryApi } = useApi("/aeps/subhearn/bank5/balance-enquiry", "POST");
  const { callApi: cashWithdrawalApi } = useApi("/aeps/subhearn/bank5/cash-withdrawal", "POST");
  const { callApi: miniStatementApi  } = useApi("/aeps/subhearn/bank5/mini-statement",  "POST");

  const [popup, setPopup] = useState({ show: false, title: "", message: "" });

  const closePopup = () => {
    if (popup.show) setClearFormTrigger((p) => p + 1);
    setPopup({ show: false, title: "", message: "" });
  };

  /* ── PID XML → biometricData object ── */
  const parsePidXml = (xml) => {
    const $xml = $(xml);
    const get  = (sel, attr) => $xml.find(sel).attr(attr) || "0";
    const infoValue = (name) =>
      $xml.find(`Info[name='${name}']`).attr("value") ||
      $xml.find(`Param[name='${name}']`).attr("value") || "0";

    return {
      dc: get("DeviceInfo", "dc"),
      ci: get("Skey", "ci"),
      hmac: $xml.find("Hmac").text() || "0",
      dpId: get("DeviceInfo", "dpId"),
      mc: get("DeviceInfo", "mc"),
      pidDataType: get("Data", "type"),
      sessionKey: $xml.find("Skey").text() || "0",
      mi: get("DeviceInfo", "mi"),
      rdsId: get("DeviceInfo", "rdsId"),
      errCode: get("Resp", "errCode"),
      errInfo: get("Resp", "errInfo"),
      fCount: get("Resp", "fCount"),
      fType: get("Resp", "fType"),
      iCount: get("Resp", "iCount"), // blank hua toh 0 jayega
      iType: get("Resp", "iType"),
      pCount: get("Resp", "pCount"),
      pType: "0",
      srno: infoValue("srno"),
      sysid: infoValue("sysid"),
      ts: infoValue("ts"),
      pidData: $xml.find("Data").text() || "0",
      qScore: get("Resp", "qScore"),
      nmPoints: get("Resp", "nmPoints"),
      rdsVer: get("DeviceInfo", "rdsVer"),
    };
  };

  /* ── Main capture handler ── */
  const handleCapture = async (formData) => {
    try {
      setProcessing(true);
      setPreviousData({ ...formData });

      /* ── ₹5,000 CW limit ── */
      if (formData.txnType === "CW" && Number(formData.amount) > 5000) {
        setProcessing(false);
        setPopup({ show: true, title: "Not Allowed", message: "Cash withdrawal above ₹5,000 is not allowed." });
        return;
      }

      const pidXml = await captureFingerprint("");

      if (!pidXml || !pidXml.includes('errCode="0"')) {
        setPopup({ show: true, title: "Failed", message: "Fingerprint Capture Failed" });
        return;
      }

      const pidData      = parsePidXml(pidXml);
      const referenceKey = generateRef();
      const ts           = generateTs();

      const baseRequest = {
        mid:          userId,
        mobile:       formData.mobile,
        aadhaar:      formData.aadhaar,
        bankIin:      formData?.bank?.iin,
        referenceKey,
        latitude,
        longitude,
        ts,
        biometricData: pidData,
      };

      /* ── Balance Enquiry ── */
      if (formData.txnType === "BE") {
        const response = await balanceEnquiryApi(baseRequest);

        if (response?.statuscode === "TXN" || response?.status === "SUCCESS") {
          setPopup({
            show: true,
            title: "Balance Enquiry Success",
            message:
              `Bank Name : ${response?.data?.bankName || "—"}\n\n` +
              `Balance : ₹${response?.data?.bankAccountBalance ?? response?.balance ?? "—"}`,
          });
        } else {
          setPopup({
            show: true,
            title: "Transaction Failed",
            message: response?.reason || response?.message || response?.status || "Transaction Failed",
          });
        }
      }

      /* ── Cash Withdrawal ── */
      if (formData.txnType === "CW") {
        const response = await cashWithdrawalApi({ ...baseRequest, amount: formData.amount });

        if (response?.statuscode === "TXN" || response?.status === "SUCCESS") {
          setPopup({
            show: true,
            title: "Cash Withdrawal Success",
            message:
              `Bank Name : ${response?.data?.bankName || "—"}\n\n` +
              `Withdraw Amount : ₹${response?.amount || formData.amount}\n\n` +
              `Bank RRN : ${response?.bank_rrn || response?.bankRRN || "—"}\n` +
              `Aadhaar No : ${response?.aadhaarNo || "—"}`,
          });
        } else {
          setPopup({
            show: true,
            title: "Failed",
            message: response?.reason || response?.message || response?.status || "Cash Withdrawal Failed",
          });
        }
      }

      /* ── Mini Statement ── */
      if (formData.txnType === "MS") {
        const response = await miniStatementApi(baseRequest);

        if (response?.statuscode === "TXN" || response?.status === "SUCCESS") {
          const rows  = response?.data?.miniStatement || [];
          const lines = rows.length > 0
            ? rows.slice(0, 5).map((r) => ` ${r.txnType} | ₹${r.amount}`).join("\n")
            : "No Mini Statement Available";

          setPopup({
            show: true,
            title: "Mini Statement Success",
            message:
              `Available Balance : ₹${response?.data?.bankAccountBalance || "—"}\n\n` +
              `Recent Transactions:\n${lines}`,
          });
        } else {
          setPopup({
            show: true,
            title: "Failed",
            message: response?.reason || response?.message || response?.status || "Mini Statement Failed",
          });
        }
      }

    } catch (err) {
      console.error("SubhearnBank5 AEPS Error", err);
      setPopup({
        show: true, title: "Error",
        message: err?.message || "Transaction Failed",
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <DeviceStatus device={device} />

      <SubhearnBank5AepsForm
        onSubmit={handleCapture}
        processing={processing}
        loading={loading}
        previousData={previousData}
        clearFormTrigger={clearFormTrigger}
      />

      {/* Result popup */}
      {popup.show && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-8 w-[420px] text-center">
            <h2 className="text-2xl font-semibold">{popup.title}</h2>
            <p className="mt-5 whitespace-pre-line text-[18px] font-bold text-gray-700 leading-8">
              {popup.message}
            </p>
            <button
              onClick={closePopup}
              className="mt-6 bg-blue-600 hover:bg-blue-700 text-white px-10 py-3 rounded-xl"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubhearnBank5AepsIntegration;
